import React, { useEffect, useState, forwardRef } from "react";
import { TextField } from "@mui/material";
import "../../theme-vars.css";
import { styled } from "@mui/material/styles";

type Props = {
  title: string;
  style?: React.CSSProperties;
  validator?: (value: string) => string;
  length?: boolean;
  disabled?: boolean;
  value?: number | string;
  required?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const primaryColor = "#292744";
const CustomTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    fontSize: "var(--font-size-large)",
    fontFamily: "var(--font-family-main)",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
  },
  "& .MuiInputBase-input": {
    padding: "14px",
    fontSize: 16,
    color: "#222",
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    fontSize: 15,
    color: primaryColor,
  },
  "& .MuiInputLabel-root.Mui-required .MuiInputLabel-asterisk": {
    color: "#d32f2f", // red color for required asterisk
  },
});

const InputText = forwardRef<HTMLInputElement, Props>(
  ({ title, style, validator, disabled, value = "", required, onChange },ref) => {
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
      if (validator) {
        setErrorMsg(validator(String(value)));
      }
    }, [value, validator]);

    return (
      <CustomTextField
        label={title}
        fullWidth
        required={required}
        variant="outlined"
        value={value}
        type="number"
        onChange={(e: any) => {
          onChange?.(e);
          if (validator) {
            setErrorMsg(validator(String(e.target.value)));
          }
        }}
        onBlur={() => {
          if (validator) {
            setErrorMsg(validator(String(value)));
          }
        }}
        error={!!errorMsg}
        helperText={errorMsg}
        InputLabelProps={{
          style: {
            color: "#555",
            fontFamily: "var(--font-family-main)",
            fontSize: "var(--font-size-medium)",
          },
        }}
        InputProps={{
          style: {
            color: "#222",
            fontFamily: "var(--font-family-main)",
            fontSize: "var(--font-size-medium)",
          },
        }}
        disabled={disabled}
        sx={style}
      />
    );
  }
);

export default InputText;
